import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../services/apiClient';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import { useToast } from '../../components/common/Toast';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import { useAutoFocoPrimerCampo } from '../../hooks/useNavegacionTeclado';

const TitleWrap = styled.div``;

const Desc = styled.p`
  margin: 0; font-size: 0.78rem; color: ${({ theme }) => theme.colors.txtSecundario};
`;

const FilterSelect = styled.select`
  background: ${({ theme }) => theme.colors.bgSecundario};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.4rem 0.65rem; border-radius: 4px; font-size: 0.8rem; cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;

const Badge = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 600;
  background: ${({ $on, theme }) => $on ? `${theme.colors.success}22` : theme.colors.bgSecundario};
  color: ${({ $on, theme }) => $on ? theme.colors.success : theme.colors.txtSecundario};
  border: 1px solid ${({ $on, theme }) => $on ? `${theme.colors.success}55` : theme.colors.borderDefault};
`;

const StyledSelect = styled.select`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem; min-height: 34px; cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;

const CheckRow = styled.label`
  display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const EMPTY_FORM = { rol: 'tractor', categoria: '', tarifa_corta_override: '', tarifa_larga_override: '', tarifa_unica_override: '', activo: true };

const ROL_LABEL = { tractor: 'Unidad motora', remolque: 'Remolque / Semi' };

export default function ConfiguracionFlotaTransporte() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [equipos, setEquipos] = useState([]);
  const [flota, setFlota] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroFlota, setFiltroFlota] = useState('todos');
  const [filtroFamilia, setFiltroFamilia] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [modal, setModal] = useState(false);
  const [equipoModal, setEquipoModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [eqResp, flotaResp, catResp, famResp] = await Promise.all([
        apiClient.get('equipos/?page_size=10000&lite=true'),
        apiClient.get('flota-transporte/?page_size=10000'),
        apiClient.get('categorias-logistica/'),
        apiClient.get('familias/?page_size=10000'),
      ]);
      setEquipos(Array.isArray(eqResp.data) ? eqResp.data : (eqResp.data.results || []));
      setFlota(Array.isArray(flotaResp.data) ? flotaResp.data : (flotaResp.data.results || []));
      setCategorias(Array.isArray(catResp.data) ? catResp.data : (catResp.data.results || []));
      setFamilias(Array.isArray(famResp.data) ? famResp.data : (famResp.data.results || []));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const flotaByEquipo = useMemo(() => {
    const m = {};
    flota.forEach(f => {
      const eqId = f.equipo?.id ?? f.equipo;
      if (eqId != null) m[String(eqId)] = f;
    });
    return m;
  }, [flota]);

  const equiposConFlota = useMemo(() => {
    return equipos.map(eq => ({
      ...eq,
      _flota: flotaByEquipo[String(eq.id)] || null,
    }));
  }, [equipos, flotaByEquipo]);

  const filtrados = useMemo(() => {
    let base = equiposConFlota;
    if (filtroFlota === 'flota') base = base.filter(e => e._flota);
    else if (filtroFlota === 'no_flota') base = base.filter(e => !e._flota);
    if (filtroFamilia) base = base.filter(e => String(e.familia ?? '') === filtroFamilia);
    if (filtroRol) base = base.filter(e => e._flota?.rol === filtroRol);
    if (!busqueda.trim()) return base;
    const q = busqueda.toLowerCase();
    return base.filter(e =>
      e.interno?.toLowerCase().includes(q) ||
      e.interno_alternativo?.toLowerCase().includes(q) ||
      e.descripcion?.toLowerCase().includes(q) ||
      e.marca_nombre?.toLowerCase().includes(q) ||
      e.modelo_nombre?.toLowerCase().includes(q)
    );
  }, [equiposConFlota, busqueda, filtroFlota, filtroFamilia, filtroRol]);

  const abrirEditar = (equipo, flotaEntry) => {
    setEquipoModal(equipo);
    setForm({
      rol: flotaEntry?.rol || 'tractor',
      categoria: flotaEntry?.categoria ? String(flotaEntry.categoria?.id ?? flotaEntry.categoria) : (categorias[0]?.id ? String(categorias[0].id) : ''),
      tarifa_corta_override: flotaEntry?.tarifa_corta_override != null ? String(flotaEntry.tarifa_corta_override) : '',
      tarifa_larga_override: flotaEntry?.tarifa_larga_override != null ? String(flotaEntry.tarifa_larga_override) : '',
      tarifa_unica_override: flotaEntry?.tarifa_unica_override != null ? String(flotaEntry.tarifa_unica_override) : '',
      activo: flotaEntry ? flotaEntry.activo !== false : true,
    });
    setErrors({});
    setModal(true);
  };

  const cerrar = () => { setModal(false); setEquipoModal(null); };
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  const agregar = async (equipo) => {
    if (!categorias.length) { toast.warning('Primero crea una categoría de logística.'); return; }
    abrirEditar(equipo, null);
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        equipo: equipoModal.id,
        rol: form.rol,
        categoria: form.categoria ? parseInt(form.categoria) : null,
        tarifa_corta_override: form.tarifa_corta_override !== '' ? parseFloat(form.tarifa_corta_override) : null,
        tarifa_larga_override: form.tarifa_larga_override !== '' ? parseFloat(form.tarifa_larga_override) : null,
        tarifa_unica_override: form.tarifa_unica_override !== '' ? parseFloat(form.tarifa_unica_override) : null,
        activo: form.activo,
      };
      const flotaEntry = flotaByEquipo[String(equipoModal.id)];
      if (flotaEntry) {
        await apiClient.patch(`flota-transporte/${flotaEntry.id}/`, payload);
      } else {
        await apiClient.post('flota-transporte/', payload);
      }
      cerrar(); cargar();
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') setErrors(detail);
    } finally { setSaving(false); }
  };

  const quitarDeFlota = async (flotaId) => {
    try { await apiClient.delete(`flota-transporte/${flotaId}/`); setConfirmId(null); cargar(); }
    catch (e) { console.error(e); }
  };

  const categoriaLabel = (id) => categorias.find(c => String(c.id) === String(id))?.nombre || '-';
  const categoriaSeleccionada = categorias.find(c => String(c.id) === String(form.categoria));

  // Al abrir el modal el cursor va al primer campo, para poder cargar sin buscarlo.
  const modalBodyRef = useRef(null);
  useAutoFocoPrimerCampo(modalBodyRef, Boolean(modal && equipoModal));

  return (
    <Page>
      <Header $alignItems="flex-start">
        <TitleWrap>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title><i className="fas fa-truck-moving" /> Flota de Transporte</Title>
            <HelpToggleBtn
              type="button"
              $active={helpOpen}
              onClick={() => setHelpOpen(v => !v)}
            >
              <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
              Ayuda
            </HelpToggleBtn>
          </div>
          <Desc>Marcá equipos del parque como unidades de transporte y asignales tarifa de logística.</Desc>
        </TitleWrap>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            prereqs={[
              'Tener creadas las tarifas de logística para asignarle una categoría a cada unidad.',
            ]}
            tips={[
              'Solo los equipos marcados acá aparecen como transporte disponible al programar un traslado.',
              'La unidad motora es la que arrastra; el remolque o semi es lo que se engancha.',
              'La categoría de tarifa define con qué valores se calcula el costo de cada viaje.',
            ]}
            nextSteps={[
              'Programar traslados asignando las unidades de la flota.',
              'Consultar el informe de costos de logística para ver el gasto por unidad.',
            ]}
            inlineOpen
          />
        </div>
      )}
      <Controls $wrap>
        <SearchInput placeholder="Buscar equipo..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <FilterSelect value={filtroFlota} onChange={e => setFiltroFlota(e.target.value)}>
          <option value="todos">Todos los equipos</option>
          <option value="flota">Solo flota de transporte</option>
          <option value="no_flota">No están en la flota</option>
        </FilterSelect>
        <FilterSelect value={filtroFamilia} onChange={e => setFiltroFamilia(e.target.value)}>
          <option value="">Todas las familias</option>
          {familias.map(f => <option key={f.id} value={String(f.id)}>{f.nombre}</option>)}
        </FilterSelect>
        <FilterSelect value={filtroRol} onChange={e => setFiltroRol(e.target.value)}>
          <option value="">Todos los roles</option>
          <option value="tractor">Unidad motora</option>
          <option value="remolque">Remolque / Semi</option>
        </FilterSelect>
        <BackBtn style={{ marginLeft: 'auto' }} onClick={() => navigate('/configuracion?seccion=logistica')}>
          <i className="fas fa-arrow-left" /> Volver
        </BackBtn>
      </Controls>
      <TableWrap $offset="175px">
        <Table>
          <thead>
            <tr>
              <Th>Interno</Th>
              <Th>Descripción</Th>
              <Th>Marca / Modelo</Th>
              <Th>Flota</Th>
              <Th>Rol</Th>
              <Th>Categoría</Th>
              <Th>Activo</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><Td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>}
            {!loading && filtrados.length === 0 && <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Sin equipos</Td></tr>}
            {filtrados.map(eq => {
              const f = eq._flota;
              return (
                <tr key={eq.id}>
                  <Td><strong>{eq.interno || '-'}</strong></Td>
                  <Td>{eq.descripcion || '-'}</Td>
                  <Td>{[eq.marca_nombre, eq.modelo_nombre].filter(Boolean).join(' ') || '-'}</Td>
                  <Td><Badge $on={!!f}>{f ? 'En flota' : 'No'}</Badge></Td>
                  <Td>{f ? (ROL_LABEL[f.rol] || f.rol) : '-'}</Td>
                  <Td>{f ? categoriaLabel(f.categoria?.id ?? f.categoria) : '-'}</Td>
                  <Td>{f ? <Badge $on={f.activo !== false}>{f.activo !== false ? 'Activo' : 'Inactivo'}</Badge> : '-'}</Td>
                  <ActionsCell>
                    {f ? (
                      <>
                        <ActionBtn onClick={() => abrirEditar(eq, f)}>Editar</ActionBtn>
                        <ActionBtn $danger onClick={() => setConfirmId(f.id)}>Quitar</ActionBtn>
                      </>
                    ) : (
                      <ActionBtn onClick={() => agregar(eq)}>+ Agregar a flota</ActionBtn>
                    )}
                  </ActionsCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {modal && equipoModal && (
        <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="540px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>
                {flotaByEquipo[String(equipoModal.id)] ? 'Editar en flota' : 'Agregar a flota'}
                &nbsp;— {equipoModal.interno}
              </ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody ref={modalBodyRef}>
              <FormGrp>
                <Lbl>Rol en la flota *</Lbl>
                <StyledSelect value={form.rol} onChange={e => set('rol', e.target.value)}>
                  <option value="tractor">Unidad motora (tractor / camión)</option>
                  <option value="remolque">Remolque / Semirremolque</option>
                </StyledSelect>
                {errors.rol && <ErrMsg>{errors.rol}</ErrMsg>}
              </FormGrp>
              <FormGrp>
                <Lbl>Categoría de tarifa *</Lbl>
                <StyledSelect value={form.categoria} onChange={e => set('categoria', e.target.value)}>
                  <option value="">— Seleccioná una categoría —</option>
                  {categorias.map(c => (
                    <option key={c.id} value={String(c.id)}>
                      {c.tarifa_unificada
                        ? `${c.nombre} (única: $${c.tarifa_unica}/km)`
                        : `${c.nombre} (corta: $${c.tarifa_corta}/km · larga: $${c.tarifa_larga}/km)`}
                    </option>
                  ))}
                </StyledSelect>
                {errors.categoria && <ErrMsg>{errors.categoria}</ErrMsg>}
              </FormGrp>
              {categoriaSeleccionada?.tarifa_unificada ? (
                <FormGrp>
                  <Lbl>Tarifa única prioritaria ($/km)</Lbl>
                  <Inp
                    type="number" min="0" step="0.01"
                    value={form.tarifa_unica_override}
                    onChange={e => set('tarifa_unica_override', e.target.value)}
                    placeholder="Dejar vacío para usar la de la categoría"
                  />
                </FormGrp>
              ) : (
                <Row2>
                  <FormGrp>
                    <Lbl>Tarifa corta distancia prioritaria ($/km)</Lbl>
                    <Inp
                      type="number" min="0" step="0.01"
                      value={form.tarifa_corta_override}
                      onChange={e => set('tarifa_corta_override', e.target.value)}
                      placeholder="Dejar vacío para usar la de la categoría"
                    />
                  </FormGrp>
                  <FormGrp>
                    <Lbl>Tarifa larga distancia prioritaria ($/km)</Lbl>
                    <Inp
                      type="number" min="0" step="0.01"
                      value={form.tarifa_larga_override}
                      onChange={e => set('tarifa_larga_override', e.target.value)}
                      placeholder="Dejar vacío para usar la de la categoría"
                    />
                  </FormGrp>
                </Row2>
              )}
              <small style={{ marginTop: '-0.4rem', display: 'block', marginBottom: '0.75rem' }}>
                Las tarifas prioritarias tienen prioridad sobre las de la categoría para este equipo específico.
              </small>
              <FormGrp>
                <CheckRow>
                  <input type="checkbox" checked={form.activo} onChange={e => set('activo', e.target.checked)} />
                  Unidad activa en la flota
                </CheckRow>
              </FormGrp>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="380px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Quitar de la flota</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody><p>¿Quitar este equipo de la flota de transporte? No se elimina el equipo, solo su registro de flota.</p></ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => quitarDeFlota(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}
    </Page>
  );
}
